import axios, {
  AxiosRequestConfig,
  AxiosResponse
} from 'axios';
import {
  ADMIN_REALM_AUTH_MODE,
  LEGACY_ADMIN_AUTH_MODE,
  isTypedAdminRealmFeatureDisabled
} from './adminRealmAuth';
import {
  MULTI_COMPANY_CONTRACT_VERSION,
  parseMultiCompanySuccessEnvelope
} from '../contracts/multiCompany/v1';

export const ADMIN_REALM_FORGOT_PASSWORD_PATH =
  '/auth/admin/forgot-password';
export const ADMIN_REALM_RESET_PASSWORD_PATH = '/auth/admin/reset-password';
export const LEGACY_ADMIN_FORGOT_PASSWORD_PATH = '/auth/forgot-password';
export const LEGACY_ADMIN_VERIFY_OTP_PATH = '/auth/verify-otp';
export const LEGACY_ADMIN_RESET_PASSWORD_PATH = '/auth/reset-password';

export type AdminRecoveryAuthMode =
  | typeof ADMIN_REALM_AUTH_MODE
  | typeof LEGACY_ADMIN_AUTH_MODE;

export type AdminRecoveryResponse = AxiosResponse & {
  authMode: AdminRecoveryAuthMode;
};

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export class AdminRealmRecoveryContractError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'AdminRealmRecoveryContractError';
  }
}

const parseEnabledRecoveryResponse = (
  value: unknown,
  expectedResult: 'recoveryRequested' | 'passwordReset'
) => {
  const envelope = parseMultiCompanySuccessEnvelope(value);
  if (
    envelope?.contractVersion !== MULTI_COMPANY_CONTRACT_VERSION ||
    envelope.capabilities.surface_identity_realms !== true ||
    !isRecord(envelope.data) ||
    envelope.data[expectedResult] !== true
  ) {
    throw new AdminRealmRecoveryContractError(
      'Admin realm recovery response is missing its required contract.'
    );
  }
};

const withAuthMode = (
  response: AxiosResponse,
  authMode: AdminRecoveryAuthMode
): AdminRecoveryResponse => ({
  ...response,
  authMode
});

const postLegacyRecovery = async (
  path: string,
  data: unknown,
  config?: AxiosRequestConfig
): Promise<AdminRecoveryResponse> =>
  withAuthMode(
    await axios.post(path, data, config),
    LEGACY_ADMIN_AUTH_MODE
  );

const postRealmRecovery = async (
  realmPath: string,
  legacyPath: string,
  expectedResult: 'recoveryRequested' | 'passwordReset',
  data: unknown,
  config?: AxiosRequestConfig,
  allowDisabledFallback = true
): Promise<AdminRecoveryResponse> => {
  try {
    const response = await axios.post(realmPath, data, config);
    parseEnabledRecoveryResponse(response.data, expectedResult);
    return withAuthMode(response, ADMIN_REALM_AUTH_MODE);
  } catch (error) {
    if (
      !allowDisabledFallback ||
      !isTypedAdminRealmFeatureDisabled(error)
    ) {
      throw error;
    }
  }

  return postLegacyRecovery(legacyPath, data, config);
};

export const requestAdminPasswordRecovery = (
  data: unknown,
  config?: AxiosRequestConfig
): Promise<AdminRecoveryResponse> =>
  postRealmRecovery(
    ADMIN_REALM_FORGOT_PASSWORD_PATH,
    LEGACY_ADMIN_FORGOT_PASSWORD_PATH,
    'recoveryRequested',
    data,
    config
  );

export const resetAdminPassword = (
  data: unknown,
  config?: AxiosRequestConfig,
  authMode?: AdminRecoveryAuthMode
): Promise<AdminRecoveryResponse> => {
  if (
    authMode !== undefined &&
    authMode !== ADMIN_REALM_AUTH_MODE &&
    authMode !== LEGACY_ADMIN_AUTH_MODE
  ) {
    throw new AdminRealmRecoveryContractError(
      'Admin password reset is missing a valid recovery mode.'
    );
  }

  if (authMode === LEGACY_ADMIN_AUTH_MODE) {
    return postLegacyRecovery(
      LEGACY_ADMIN_RESET_PASSWORD_PATH,
      data,
      config
    );
  }

  return postRealmRecovery(
    ADMIN_REALM_RESET_PASSWORD_PATH,
    LEGACY_ADMIN_RESET_PASSWORD_PATH,
    'passwordReset',
    data,
    config,
    authMode !== ADMIN_REALM_AUTH_MODE
  );
};
